/**
 * The seats of one work session, as a call states them.
 *
 * A v2 project seats a musician PER PIECE, so a work-session override produces
 * one chair per piece — three pieces, three chairs, identical in every respect a
 * call shows. Rendering them straight printed "Associate Principal — Flute ·
 * Associate Principal — Flute · Associate Principal — Flute" and the same memo
 * three times under it.
 *
 * Collapsing on the rendered tuple keeps the genuinely informative case — a
 * musician who is principal on one piece and section on another still gets both
 * — while saying a single arrangement once.
 *
 * This mirrors mercury's callChairs (mercurysrv/modernCall.go) on purpose: the
 * email and this page are meant to be the same document, so they collapse the
 * same way. It is NOT done in rhapsody-service's mapping, which both consume,
 * because the legacy call page uses the per-chair PieceNumber to label "Chair
 * for Piece #N" and collapsing upstream would take that away.
 */
export type CallSeat = {
  role: string;
  section: string;
  /** The chair's instruments, joined — a doubling is why a seat lists more than one. */
  instruments: string;
  memo: string;
};

type ChairLike = {
  SectionRole?: string;
  SectionName?: string;
  Instruments?: string[];
  Memo?: string;
};

/**
 * @param {ChairLike[]} chairs the engagement's chairs, in any order
 * @returns {CallSeat[]} the distinct seats, empty ones dropped
 */
export function callSeats(chairs?: readonly ChairLike[]): CallSeat[] {
  const seen = new Set<string>();
  const res: CallSeat[] = [];

  for (const chair of chairs ?? []) {
    const seat: CallSeat = {
      role: chair.SectionRole ?? "",
      section: chair.SectionName ?? "",
      instruments: (chair.Instruments ?? []).join(", "),
      memo: chair.Memo ?? "",
    };

    // A seat with nothing to say is not a row.
    if (!seat.role && !seat.section && !seat.instruments && !seat.memo) continue;

    const key = JSON.stringify(seat);
    if (seen.has(key)) continue;

    seen.add(key);
    res.push(seat);
  }

  return res;
}

/** The seat's one-line description: "Associate Principal — Flute · Flute, Piccolo". */
export function callSeatLabel(seat: CallSeat): string {
  return [
    [seat.role, seat.section].filter(Boolean).join(" — "),
    seat.instruments,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** What a session's seating IS, memos excluded — a memo is news about that
 * session, not part of the answer to "where do I sit". */
function seatingKey(seats: CallSeat[]): string {
  return seats.map(callSeatLabel).join(" · ");
}

export type CallSeatPlan = {
  /** The seating almost every session shares, stated once above the list. */
  common: string;
  /** True when this session's seating differs from `common`, or it has a memo. */
  standsOut: (seats: CallSeat[]) => boolean;
};

/**
 * Decides what to say where.
 *
 * Repeating "Section — Violin I · Violin" under every session, in a colour that
 * means "look at this", says nothing — it is the same seat all the way down. The
 * thing worth a box is the session that DIFFERS: the one where the role, the
 * instruments or the instruction are not what the rest of the call agreed.
 *
 * So the seating the sessions have in common is stated once, plainly, above the
 * list, and a session only gets the amber box when it deviates from it — or when
 * it carries a memo, which is news whether or not the seat changed.
 *
 * "In common" is the strictly most frequent seating. On a tie there is no norm to
 * state — two sessions seated two different ways have no default between them —
 * so nothing goes on top and both are boxed.
 *
 * @param {object[]} engagements the call's work sessions
 * @returns {CallSeatPlan} the common seating, and a test for standing out
 */
export function callSeatPlan(
  engagements?: readonly { Chairs?: readonly ChairLike[] }[],
): CallSeatPlan {
  const counts = new Map<string, number>();

  for (const engagement of engagements ?? []) {
    const key = seatingKey(callSeats(engagement.Chairs));
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  let common = "";
  let best = 0;
  let tied = false;

  for (const [key, count] of counts) {
    if (count > best) {
      best = count;
      common = key;
      tied = false;
    } else if (count === best) {
      tied = true;
    }
  }

  if (tied) common = "";

  return {
    common,
    standsOut: (seats) =>
      seats.some((seat) => Boolean(seat.memo)) || seatingKey(seats) !== common,
  };
}
